import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import LoaderEventLanding from "../../components/Loader/LoaderEventLanding";
import CustomLandingPage from "../../components/Common/EventLanding/CustomLandingPage";
import useCustomFecthQuery from "../../Hooks/useCustomFecthQuery";

/**
 * Preview page for landing page builder
 * STRICTLY uses landing page preview data
 * NO fallback to event data
 * Uses POST request to fetch preview data
 */
const EventLandingPreview = () => {
  document.title = "Kago Events - Preview";

  const { id: eventID } = useParams();
  const [previewData, setPreviewData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Safely get token
  let token = null;
  try {
    const authData = localStorage.getItem("authEventKagoUser");
    if (authData) {
      const parsed = JSON.parse(authData);
      token = parsed?.data?.token || null;
    }
  } catch (err) {
    console.error("Failed to read auth token", err);
  }

  // Fetch registration form data
  const {
    data: registrationFormData,
    isLoading: isLoadingRegistrationForm,
  } = useCustomFecthQuery(
    ['eventForm', eventID],
    `${process.env.REACT_APP_API}event-form/${eventID}`,
    token,
    { enabled: !!eventID && !!token }
  );

  // Fetch ticket settings
  const {
    data: ticketSettingsData,
    isLoading: isLoadingTickets,
  } = useCustomFecthQuery(
    ['ticketSettings', eventID],
    `${process.env.REACT_APP_API}ticket-settings/${eventID}/event`,
    token,
    { enabled: !!eventID && !!token }
  );

  // Fetch sessions
  const {
    data: sessionsData,
    isLoading: isLoadingSessions,
  } = useCustomFecthQuery(
    ['guestSessions', eventID],
    `${process.env.REACT_APP_API}guest/sessions?eid=${eventID}`,
    token,
    { enabled: !!eventID && !!token }
  );

  // Fetch event details to get support information
  const {
    data: eventDetailsData,
    isLoading: isLoadingEventDetails,
  } = useCustomFecthQuery(
    ['myEventDetail', eventID],
    `${process.env.REACT_APP_API}guest/events/${eventID}`,
    null,
    { enabled: !!eventID }
  );

  // Debug: Log registration form data
  console.log('[EventLandingPreview] Registration Form Data:', {
    hasData: !!registrationFormData,
    isLoading: isLoadingRegistrationForm,
    data: registrationFormData,
    customFields: registrationFormData?.data?.[0]?.customFields,
    customFieldsLength: registrationFormData?.data?.[0]?.customFields?.length,
    eventID,
    hasToken: !!token
  });

  // Debug: Log ticket settings data
  console.log('[EventLandingPreview] Ticket Settings Data:', {
    hasData: !!ticketSettingsData,
    isLoading: isLoadingTickets,
    data: ticketSettingsData,
    tickets: ticketSettingsData?.data,
    ticketsLength: ticketSettingsData?.data?.length,
    eventID,
    hasToken: !!token
  });

  // Debug: Log sessions data
  console.log('[EventLandingPreview] Sessions Data:', {
    hasData: !!sessionsData,
    isLoading: isLoadingSessions,
    data: sessionsData,
    sessions: sessionsData?.data,
    sessionsLength: sessionsData?.data?.length,
    eventID,
    hasToken: !!token
  });

  // Fetch landing page preview using POST
  useEffect(() => {
    if (!eventID || !token) {
      setIsLoading(false);
      return;
    }

    const fetchPreview = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const myHeaders = new Headers();
        myHeaders.append("Authorization", `Bearer ${token}`);
        myHeaders.append("Content-Type", "application/json");

        const response = await fetch(
          `${process.env.REACT_APP_API}event-landing-pages/${eventID}/preview`,
          {
            method: "POST",
            headers: myHeaders,
            body: JSON.stringify({}), // Empty body for POST request
            redirect: "follow",
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();

        if (result?.status === "success") {
          setPreviewData(result);
        } else if (result?.status === "fail" || result?.status === "error") {
          setError(result?.message || "Failed to load preview");
        } else {
          setPreviewData(result);
        }
      } catch (err) {
        console.error("Preview fetch error:", err);
        setError(err.message || "Failed to load preview");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPreview();
  }, [eventID, token]);

  // Loading state
  if (isLoading || isLoadingRegistrationForm || isLoadingTickets || isLoadingSessions || isLoadingEventDetails) {
    return <LoaderEventLanding />;
  }

  // Error state
  if (error) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <h2>Preview Unavailable</h2>
        <p>
          Unable to load landing page preview.
          <br />
          Please make sure the landing page is saved.
        </p>
      </div>
    );
  }

  const page = previewData?.data;

  // No landing page found - check various conditions
  if (!page) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <h2>No Landing Page Found</h2>
        <p>
          No landing page configuration found for this event.
          <br />
          Please go back to the builder and save your landing page before previewing.
        </p>
        <button
          onClick={() => window.close()}
          style={{
            marginTop: "20px",
            padding: "10px 20px",
            backgroundColor: "#3b82f6",
            color: "white",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
          }}
        >
          Close Preview
        </button>
      </div>
    );
  }

  // Check if required fields are missing
  if (!page.templateId || !page.eventData) {
    console.warn("Preview data incomplete:", {
      hasTemplateId: !!page.templateId,
      hasEventData: !!page.eventData,
      pageData: page,
    });
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <h2>Incomplete Landing Page Data</h2>
        <p>
          The landing page configuration is incomplete.
          <br />
          Please go back to the builder, ensure all fields are filled, and save again.
        </p>
        <button
          onClick={() => window.close()}
          style={{
            marginTop: "20px",
            padding: "10px 20px",
            backgroundColor: "#3b82f6",
            color: "white",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
          }}
        >
          Close Preview
        </button>
      </div>
    );
  }

  // Color scheme resolver
  const getColorScheme = () => {
    const schemes = [
      { id: "vibrant", colors: ["#f97316", "#fb923c", "#fdba74", "#fed7aa"] },
      { id: "professional", colors: ["#3b82f6", "#60a5fa", "#93c5fd", "#dbeafe"] },
      { id: "elegant", colors: ["#8b5cf6", "#a78bfa", "#c4b5fd", "#e9d5ff"] },
      { id: "nature", colors: ["#22c55e", "#4ade80", "#86efac", "#bbf7d0"] },
      { id: "sunset", colors: ["#f72585", "#ff006e", "#fb8500", "#ffb703"] },
    ];

    const index =
      typeof page.colorSchemeIndex === "number"
        ? page.colorSchemeIndex
        : parseInt(page.colorSchemeIndex, 10);

    return !isNaN(index) && schemes[index] ? schemes[index] : schemes[0];
  };

  // Get registration form fields
  const registrationForm = registrationFormData?.data?.[0]?.customFields || [];

  // Get event name from eventData or use a default
  const eventName = page?.eventData?.name || page?.eventData?.title || "event";

  // Prepare tickets data - transform ticket settings to tickets format
  const tickets = ticketSettingsData?.data ? ticketSettingsData.data.map(ticket => {
    // Check if early bird pricing is active
    const today = new Date();
    const isEarlyBirdActive = ticket.allowEarlyBird &&
      ticket.earlyBirdStartDate &&
      ticket.earlyBirdEndDate &&
      today >= new Date(ticket.earlyBirdStartDate) &&
      today <= new Date(ticket.earlyBirdEndDate);
    
    const ticketPrice = isEarlyBirdActive ? (ticket.tierEarlyBirdPrice || ticket.price) : (ticket.price || 0);
    
    return {
      name: ticket.tier || ticket.name || 'Ticket',
      title: ticket.tier || ticket.name || 'Ticket',
      description: ticket.description || ticket.ticketTerms || '',
      price: ticketPrice,
      features: ticket.features || [],
      id: ticket._id || ticket.id,
      isEarlyBird: isEarlyBirdActive
    };
  }) : [];

  // Prepare schedule data - transform sessions to schedule format
  const schedule = sessionsData?.data ? sessionsData.data.map(session => ({
    title: session.sessionName || session.name || session.title,
    name: session.sessionName || session.name || session.title,
    time: session.sessionTime || session.time || '',
    date: session.sessionDate || session.date || '',
    startTime: session.startTime || '',
    endTime: session.endTime || '',
    description: session.description || session.sessionDescription || '',
    location: session.location || session.sessionLocation || '',
    sessionID: session._id || session.sessionID || session.id,
    _id: session._id || session.id
  })) : [];

  // Prepare sponsors data (from landing page eventData.sponsors if present)
  const sponsors = Array.isArray(page?.eventData?.sponsors) ? page.eventData.sponsors : [];

  // ✅ DEBUG: Log logo URLs to identify which field contains the logo
  const logoFromPreview = page?.eventData?.logoImage || page?.eventData?.logo;
  const logoFromEventDetails = eventDetailsData?.data?.logoImage || eventDetailsData?.data?.logo || eventDetailsData?.data?.eventLogo;
  
  console.log('[EventLandingPreview] Logo URL Debug:', {
    'page.eventData.logoImage': page?.eventData?.logoImage,
    'page.eventData.logo': page?.eventData?.logo,
    'eventDetailsData.data.logoImage': eventDetailsData?.data?.logoImage,
    'eventDetailsData.data.logo': eventDetailsData?.data?.logo,
    'eventDetailsData.data.eventLogo': eventDetailsData?.data?.eventLogo,
    'Final selected logo': logoFromPreview || logoFromEventDetails || 'NONE'
  });

  // Debug: Log what's being passed to CustomLandingPage
  console.log('[EventLandingPreview] Passing to CustomLandingPage:', {
    hasEventData: !!page.eventData,
    templateId: page.templateId,
    registrationFormLength: registrationForm.length,
    registrationForm: registrationForm,
    willShowForm: registrationForm && registrationForm.length > 0,
    ticketsLength: tickets.length,
    tickets: tickets,
    scheduleLength: schedule.length,
    schedule: schedule,
    sponsorsLength: sponsors.length,
    sponsors,
    eventID,
    eventName,
    logoImage: logoFromPreview || logoFromEventDetails || 'EMPTY'
  });

  // Get support information from event details
  const supportMail = eventDetailsData?.data?.supportMail || '';
  const supportPhoneNumber = eventDetailsData?.data?.supportPhoneNumber || '';

  // Debug: Log support information
  console.log('[EventLandingPreview] Support Information:', {
    hasEventDetails: !!eventDetailsData,
    supportMail,
    supportPhoneNumber,
    eventDetailsData: eventDetailsData?.data
  });

  return (
    <div style={{ width: "100%", minHeight: "100vh" }}>
      
      {/* ✅ FORCE LOGO SIZE WITH CSS OVERRIDE */}
      <style>{`
        .event-logo-responsive {
          height: 45px !important;
          max-width: 150px !important;
          width: auto !important;
          object-fit: contain !important;
          display: block !important;
        }
        
        @media (min-width: 768px) {
          .event-logo-responsive {
            height: 50px !important;
            max-width: 200px !important;
          }
        }
        
        @media (min-width: 1024px) {
          .event-logo-responsive {
            height: 55px !important;
            max-width: 250px !important;
          }
        }
      `}</style>
      
      <CustomLandingPage
        eventData={{
          ...page.eventData,
          tickets: tickets,
          schedule: schedule,
          // ✅ IMPROVED: Better logo fallback chain with multiple field names
          logoImage: 
            page?.eventData?.logoImage || 
            page?.eventData?.logo || 
            eventDetailsData?.data?.logoImage || 
            eventDetailsData?.data?.logo || 
            eventDetailsData?.data?.eventLogo || 
            '',
          supportMail: supportMail,
          supportPhoneNumber: supportPhoneNumber,
          faq: page?.eventData?.faq || eventDetailsData?.data?.faq || [],
          sponsors: sponsors
        }}
        templateId={page.templateId}
        colorScheme={getColorScheme()}
        isBuilderPreview={true}
        registrationForm={registrationForm}
        eventID={eventID}
        eventName={eventName}
      />
    </div>
  );
};

export default EventLandingPreview;